import React, { useState } from 'react';
import { Loader2, CheckCircle, Send } from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EstimationForm = () => {
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    type_bateau: 'Voilier',
    marque: '',
    modele: '',
    annee: '',
    longueur: '',
    localisation: '',
    message: '',
    website: '' // honeypot
  });

  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation basique
    if (!formData.nom || !formData.email || !formData.telephone) {
      setErrorMessage('Veuillez remplir les champs obligatoires (nom, email, téléphone)');
      setStatus('error');
      return;
    }

    setStatus('loading');
    setErrorMessage('');

    try {
      const response = await fetch(`${API_URL}/api/estimation/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Erreur lors de l\'envoi');
      }

      setStatus('success');
      // Reset form after success
      setFormData({
        nom: '',
        email: '',
        telephone: '',
        type_bateau: 'Voilier',
        marque: '',
        modele: '',
        annee: '',
        longueur: '',
        localisation: '',
        message: '',
        website: ''
      });
    } catch (err) {
      setStatus('error');
      setErrorMessage(err.message);
    }
  };

  if (status === 'success') {
    return (
      <div className="bg-[#302f2c] border border-[#3f4816] rounded-xl p-8 text-center" data-testid="estimation-success">
        <CheckCircle className="w-16 h-16 text-[#d9fb06] mx-auto mb-4" />
        <h3 className="text-2xl font-bold text-[#d9fb06] mb-3">Demande envoyée !</h3>
        <p className="text-[#dfddd6] mb-6">
          Merci pour votre demande d'estimation. Nous vous recontacterons dans les plus brefs délais.
        </p>
        <Button
          onClick={() => setStatus('idle')}
          className="bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#c4e205] rounded-full px-6 py-2 font-bold"
        >
          Nouvelle demande
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-[#302f2c] border border-[#3f4816] rounded-xl p-8" data-testid="estimation-form">
      {/* honeypot */}
      <input type="text" name="website" value={formData.website} onChange={handleChange}
        className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
      <h3 className="text-2xl font-bold text-[#d9fb06] mb-6 uppercase">Formulaire d'estimation</h3>

      {status === 'error' && (
        <div className="bg-red-500/20 border border-red-500 text-red-300 p-4 rounded-lg mb-6">
          {errorMessage}
        </div>
      )}

      {/* Informations personnelles */}
      <div className="space-y-4 mb-6">
        <h4 className="text-[#dfddd6] font-semibold border-b border-[#3f4816] pb-2">Vos coordonnées *</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-[#888680] text-sm mb-1">Nom complet *</label>
            <input
              type="text"
              name="nom"
              value={formData.nom}
              onChange={handleChange}
              required
              className="w-full bg-[#1a1c1b] border border-[#3f4816] rounded-lg px-4 py-2 text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none"
              data-testid="input-nom"
            />
          </div>
          <div>
            <label className="block text-[#888680] text-sm mb-1">Email *</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full bg-[#1a1c1b] border border-[#3f4816] rounded-lg px-4 py-2 text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none"
              data-testid="input-email"
            />
          </div>
        </div>

        <div>
          <label className="block text-[#888680] text-sm mb-1">Téléphone *</label>
          <input
            type="tel"
            name="telephone"
            value={formData.telephone}
            onChange={handleChange}
            required
            className="w-full bg-[#1a1c1b] border border-[#3f4816] rounded-lg px-4 py-2 text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none"
            data-testid="input-telephone"
          />
        </div>
      </div>

      {/* Informations sur le bateau */}
      <div className="space-y-4 mb-6">
        <h4 className="text-[#dfddd6] font-semibold border-b border-[#3f4816] pb-2">Votre bateau</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-[#888680] text-sm mb-1">Type de bateau</label>
            <select
              name="type_bateau"
              value={formData.type_bateau}
              onChange={handleChange}
              className="w-full bg-[#1a1c1b] border border-[#3f4816] rounded-lg px-4 py-2 text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none"
              data-testid="input-type"
            >
              <option value="Voilier">Voilier</option>
              <option value="Catamaran">Catamaran</option>
              <option value="Bateau à moteur">Bateau à moteur</option>
              <option value="Semi-rigide">Semi-rigide</option>
              <option value="Yacht">Yacht</option>
              <option value="Autre">Autre</option>
            </select>
          </div>
          <div>
            <label className="block text-[#888680] text-sm mb-1">Marque</label>
            <input
              type="text"
              name="marque"
              value={formData.marque}
              onChange={handleChange}
              placeholder="ex: Bénéteau, Jeanneau..."
              className="w-full bg-[#1a1c1b] border border-[#3f4816] rounded-lg px-4 py-2 text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none"
              data-testid="input-marque"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-[#888680] text-sm mb-1">Modèle</label>
            <input
              type="text"
              name="modele"
              value={formData.modele}
              onChange={handleChange}
              placeholder="ex: Oceanis 40"
              className="w-full bg-[#1a1c1b] border border-[#3f4816] rounded-lg px-4 py-2 text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none"
              data-testid="input-modele"
            />
          </div>
          <div>
            <label className="block text-[#888680] text-sm mb-1">Année</label>
            <input
              type="text"
              name="annee"
              value={formData.annee}
              onChange={handleChange}
              placeholder="ex: 2015"
              className="w-full bg-[#1a1c1b] border border-[#3f4816] rounded-lg px-4 py-2 text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none"
              data-testid="input-annee"
            />
          </div>
          <div>
            <label className="block text-[#888680] text-sm mb-1">Longueur (m)</label>
            <input
              type="text"
              name="longueur"
              value={formData.longueur}
              onChange={handleChange}
              placeholder="ex: 12m"
              className="w-full bg-[#1a1c1b] border border-[#3f4816] rounded-lg px-4 py-2 text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none"
              data-testid="input-longueur"
            />
          </div>
        </div>

        <div>
          <label className="block text-[#888680] text-sm mb-1">Localisation (port ou ville)</label>
          <input
            type="text"
            name="localisation"
            value={formData.localisation}
            onChange={handleChange}
            placeholder="ex: La Rochelle, Port des Minimes"
            className="w-full bg-[#1a1c1b] border border-[#3f4816] rounded-lg px-4 py-2 text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none"
            data-testid="input-localisation"
          />
        </div>
      </div>

      {/* Message */}
      <div className="mb-6">
        <label className="block text-[#888680] text-sm mb-1">Message (optionnel)</label>
        <textarea
          name="message"
          value={formData.message}
          onChange={handleChange}
          rows={4}
          placeholder="Informations complémentaires sur votre bateau (équipements, état, heures moteur...)"
          className="w-full bg-[#1a1c1b] border border-[#3f4816] rounded-lg px-4 py-2 text-[#dfddd6] focus:border-[#d9fb06] focus:outline-none resize-none"
          data-testid="input-message"
        />
      </div>

      <Button
        type="submit"
        disabled={status === 'loading'}
        className="w-full bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#c4e205] rounded-full py-3 font-bold uppercase flex items-center justify-center gap-2"
        data-testid="submit-estimation"
      >
        {status === 'loading' ? (
          <>
            <Loader2 size={20} className="animate-spin" />
            Envoi en cours...
          </>
        ) : (
          <>
            <Send size={20} />
            Demander mon estimation gratuite
          </>
        )}
      </Button>

      <p className="text-[#888680] text-sm text-center mt-4">
        * Champs obligatoires. Vos données sont utilisées uniquement pour traiter votre demande.
      </p>
    </form>
  );
};

export default EstimationForm;
